import { useRef, useEffect } from 'react';

// qs
import qs from 'qs';
// axios
import Axios from 'axios';
// date
import { format } from 'date-fns';
// notistack
import { useSnackbar } from 'notistack';

// hooks
import useAuth from './useAuth';
// config
import { appConfig } from '../config';
import { useLoadingScreen } from '../components/LoadingScreenProvider';

// ----------------------------------------------------------------------

export default function useAxios(baseUrl) {
  // * ---------------------------------- Hooks ------------------------------------
  const { token } = useAuth();
  const { setOpenLoading } = useLoadingScreen();
  const axiosRef = useRef(null);
  const notistack = useSnackbar();

  useEffect(() => {
    const cancelSource = Axios.CancelToken.source();
    const axiosInstance = Axios.create({
      baseURL: baseUrl || appConfig.uri
    });

    axiosInstance.interceptors.request.use(
      (config) => {
        config.paramsSerializer = (params) =>
          qs.stringify(params, {
            serializeDate: (date) => format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'")
          });
        return config;
      },
      (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (Axios.isCancel(error)) return null;
        let msg = 'Something went wrong';

        if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          msg = json.message || msg;
        } else if (error.response?.data?.message) {
          msg = error.response.data.message;
        }

        if (typeof msg !== 'string') {
          msg = 'Something went wrong';
        }
        notistack.enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 10000 });
        console.dir(error);
        console.log(msg);
        return Promise.reject(msg);
      }
    );

    axiosRef.current = {
      axios: axiosInstance,
      cancelToken: cancelSource.token
    };

    return () => {
      cancelSource.cancel();
    };
  }, [baseUrl, notistack]);

  useEffect(() => {
    axiosRef.current.axios.defaults.headers.Authorization = (token && `Bearer ${token}`) || null;
  }, [token]);

  return [
    // GET
    (url, option) => {
      if (option && option.loading) {
        setOpenLoading(true);
      }

      const promise = axiosRef.current.axios.get(url, {
        ...(option || {}),
        ...(option && option.token ? { headers: { Authorization: `Bearer ${option.token}` } } : {}),
        cancelToken: axiosRef.current.cancelToken
      });

      if (option && option.loading) {
        promise.finally(() => {
          setOpenLoading(false);
        });
      }

      return promise;
    },
    // POST
    (url, data, option) => {
      if (option && option.loading) {
        setOpenLoading(true);
      }

      const promise = axiosRef.current.axios.post(url, data, {
        ...(option || {}),
        cancelToken: axiosRef.current.cancelToken
      });
      if (option && option.loading) {
        promise.finally(() => {
          setOpenLoading(false);
        });
      }

      return promise;
    },
    // PUT
    (url, data, option) => {
      if (option && option.loading) {
        setOpenLoading(true);
      }

      const promise = axiosRef.current.axios.put(url, data, {
        ...(option || {}),
        cancelToken: axiosRef.current.cancelToken
      });
      if (option && option.loading) {
        promise.finally(() => {
          setOpenLoading(false);
        });
      }

      return promise;
    },
    // DELETE
    (url, option) => {
      if (option && option.loading) {
        setOpenLoading(true);
      }

      const promise = axiosRef.current.axios.delete(url, {
        ...(option || {}),
        cancelToken: axiosRef.current.cancelToken
      });
      if (option && option.loading) {
        promise.finally(() => {
          setOpenLoading(false);
        });
      }

      return promise;
    },
    // PATCh
    (url, data, option) => {
      if (option && option.loading) {
        setOpenLoading(true);
      }

      const promise = axiosRef.current.axios.patch(url, data, {
        ...(option || {}),
        cancelToken: axiosRef.current.cancelToken
      });

      if (option && option.loading) {
        promise.finally(() => {
          setOpenLoading(false);
        });
      }

      return promise;
    }
  ];
}
